/* eslint-disable vue/max-len */
/* eslint-disable global-require */
/* eslint-disable import/no-dynamic-require */

/**
 * 通用工具类，包含一些常用的工具方法。
 */
export class ConfigProviderUtils {
  /**
   * 检查item是否为对象。
   * @param {*} item - 要检查的item。
   * @returns {boolean} - 如果item是对象则为 true，否则为 false。
   */
  static isObject(item) {
    return (item && typeof item === 'object' && !Array.isArray(item));
  }

  /**
   * 深度合并两个对象。
   * @param {Object} target - 目标对象。
   * @param {Object} source - 要合并的源对象。
   * @returns {Object} - 合并后的对象。
   */
  static mergeDeep(target, source) {
    const output = { ...target };
    if (ConfigProviderUtils.isObject(target) && ConfigProviderUtils.isObject(source)) {
      Object.keys(source).forEach((key) => {
        if (ConfigProviderUtils.isObject(source[key])) {
          if (!(key in target)) Object.assign(output, { [key]: source[key] });
          else output[key] = ConfigProviderUtils.mergeDeep(target[key], source[key]);
        } else {
          Object.assign(output, { [key]: source[key] });
        }
      });
    }
    return output;
  }
}

/**
 * 用于管理离线配置
 */
export class OfflineConfigProvider {
  /**
   * 存储离线配置的对象
   * @type {Object}
   */
  static offlineConfigs = {};

  /**
   * 初始化离线配置
   * @param {string} customConfigName - 自定义配置名称
   * @param {string} [geo='cn'] - 配置的地理位置，默认为cn
   * @returns {Object|null} - 初始化后的离线配置，如果发生错误则返回null
   */
  static initOfflineConfigs() {
    const customConfigName = process.env.brand || 'default';
    const geo = process.env.geo || 'cn';
    let configPath = '';
    try {
      configPath = customConfigName
        ? `${process.cwd()}/configs/${geo}/${customConfigName}.config.json`
        : `${process.cwd()}/configs/${geo}/default.config.json`;
      const config = require(configPath);
      if (customConfigName) {
        const defaultConfig = require(`${process.cwd()}/configs/${geo}/default.config.json`);
        OfflineConfigProvider.offlineConfigs[geo] = ConfigProviderUtils.mergeDeep(defaultConfig, config);
      } else {
        OfflineConfigProvider.offlineConfigs[geo] = config;
      }
    } catch (error) {
      console.error(`读取配置文件时发生错误：${configPath}`, error);
      return null;
    }
    return OfflineConfigProvider.offlineConfigs[geo];
  }

  static chainWebpack(config) {
    const offlineConfig = OfflineConfigProvider.initOfflineConfigs();
    config.plugin('define').tap((args) => {
      args[0] = {
        ...args[0],
        process: {
          ...args[0].process,
          config: Object.fromEntries(
            Object.entries(offlineConfig).map(([key, value]) => [key, JSON.stringify(value)]),
          ),
        },
      };
      return args;
    });
  }
}

/**
 * 用于管理运行时配置
 */
export class RuntimeConfigProvider {
  /**
   * 默认配置，初始化为 process.config 或一个空对象
   * @type {Object}
   */
  static configs = process.config || {};

  /**
   * 用于跟踪每个命名空间的注册操作的 Promise 对象
   * @type {Object}
   */
  static registrationPromise = {};

  /**
   * 异步注册配置命名空间，使用提供的 reducer 函数
   * @param {Function} reducer - 用于获取配置的 reducer 函数
   * @param {string} namespace - 配置的命名空间
   * @returns {Promise} - 一个解析为注册配置的 Promise
   */
  static async register(reducer, namespace?: string) {
    if (namespace) {
      // 如果存在注册 Promise，则等待其完成
      if (this.registrationPromise[namespace]) {
        await this.registrationPromise[namespace];
      }

      // 开始注册配置
      this.registrationPromise[namespace] = (async () => {
        const config = await reducer();
        RuntimeConfigProvider.configs[namespace] = config;
      })();

      // 等待注册完成
      await this.registrationPromise[namespace];

      // 返回注册的配置
      return RuntimeConfigProvider.configs[namespace];
    }
    // 如果没传namespace, 直接合并配置
    const config = await reducer();
    Object.assign(RuntimeConfigProvider.configs, ConfigProviderUtils.mergeDeep(process.config, config))
    // 返回注册的配置
    return RuntimeConfigProvider.configs;
  }

  
  static applyConfigToBody() {
    const brand = (process.config as any).brand;
    const geo = (process.config as any).geo;
    document.body.classList.add(`config-brand-${brand}`);
    document.body.classList.add(`config-brand-${geo}`);
  }

  /**
   * 异步获取指定命名空间的配置
   * 如果未提供命名空间，则返回整个 configs 对象的 Promise
   * @param {string} [namespace] - 配置的命名空间
   * @returns {Promise} - 请求的配置的Promise 或整个 configs 对象的 Promise
   */
  static async getConfigAsync(namespace?: string) {
    if (namespace === undefined) {
      // 如果未提供命名空间，则返回一个解析为整个 configs 对象的 Promise
      return Promise.resolve(RuntimeConfigProvider.configs);
    }

    const config = RuntimeConfigProvider.configs[namespace];
    if (config !== undefined) {
      console.log(`同步获取 ${namespace} 的配置：`, config);
      return config;
    } if (this.registrationPromise[namespace]) {
      // 如果注册 Promise 存在，则等待其完成
      await this.registrationPromise[namespace];
      console.log(`注册 ${namespace} 配置已完成`);
      return Promise.resolve(RuntimeConfigProvider.configs[namespace]);
    }
    console.warn(`同步获取 ${namespace} 配置失败：未找到配置`);
    return undefined;
  }
}

// module.exports = {
//   OfflineConfigProvider,
//   RuntimeConfigProvider,
//   ConfigProviderUtils,
// };
